import classNames from 'classnames'
import { useState } from 'react'
import { MdInfoOutline } from 'react-icons/md'

import type { ReactElement } from 'react'
import type { TScore } from '~/types/cluster'

import ScoreTable from './ScoreTable'

import CollapseButton from '~/components/Buttons/CollapseButton'
import SkeletonLoader from '~/components/SkeletonLoader'
import Tooltip from '~/components/Tooltip'
import { useClustersContext } from '~/providers/Clusters'
import { useClusterStatsQuery } from '~/queries/clusters'
import { formatValue } from '~/utils/number'

import './styles.scss'

interface Props {
  clusterId: string
  nbSelectedApps: number
}

const getColor = (value: number) =>
  value <= 0 ? '#FF2E26' : value <= 7 ? '#F7A23B' : value <= 14 ? '#FBC62F' : '#0FCA7A'

const getPercentScoreColor = (value: number) =>
  value <= 25 ? '#FF2E26' : value <= 50 ? '#F7A23B' : value <= 75 ? '#FBC62F' : '#0FCA7A'

const ClusterScoreBadge = ({
  className,
  scale,
  tooltipScores,
  value,
}: {
  className?: string
  scale?: 25 | 100
  tooltipScores?: TScore[]
  value: number | null
}): ReactElement => {
  return (
    <div
      className={classNames('cluster-score-badge-component', className)}
      style={{
        background: value !== null ? (scale === 25 ? getColor(value) : getPercentScoreColor(value)) : '#000',
      }}
    >
      <span className="cluster-score-badge-component__main-score">{value !== null ? value : '?'}</span>
      {tooltipScores && tooltipScores.length > 0 ? (
        <Tooltip
          className="cluster-score-badge-component__tooltip"
          overlay={
            <ul>
              {tooltipScores.map(({ label, value }, idx) => (
                <li className="cluster-score-badge-component__score-item" key={idx}>
                  {label}: <span className="cluster-score-component__tooltip-score-item__value">{value}</span>
                </li>
              ))}
            </ul>
          }
          placement="top"
          trigger="hover"
        >
          <div className="cluster-score-badge-component__info-icon-wrapper">
            <MdInfoOutline color="#aeaeae" size={13} />
          </div>
        </Tooltip>
      ) : null}
    </div>
  )
}

const MetricsBlock = ({ clusterId, nbSelectedApps }: Props): ReactElement => {
  /** Local state */

  const { state } = useClustersContext()

  const [isTableCollapsed, setIsTableCollapsed] = useState(false)

  const detailsClasses = classNames('metrics-block-component__details-content', {
    'metrics-block-component__details-content--visible': !isTableCollapsed,
  })

  /** Queries & Mutations */

  const { data: metrics, isLoading: isStatsLoading } = useClusterStatsQuery(clusterId)

  /** Handlers */

  const handleToggleClick = (toggleState: boolean) => () => setIsTableCollapsed(toggleState)

  /** Render */

  return isStatsLoading ? (
    <div className="metrics-block-skeleton">
      <SkeletonLoader />
    </div>
  ) : metrics ? (
    <div className="metrics-block-component">
      <div className="metrics-block-component__main-content">
        <div className="metrics-block-component__main-content__nb-selected-apps">
          {nbSelectedApps > 0 ? `${nbSelectedApps} app${nbSelectedApps > 1 ? 's' : ''} selected` : ''}
        </div>
        <ClusterScoreBadge
          className="metric-tuple metric-tuple-score"
          tooltipScores={[
            { label: 'Revenue D360', value: metrics.score_revenue_d360 },
            { label: 'Revenue D360 US', value: metrics.score_revenue_d360_us },
            { label: 'Downloads D360', value: metrics.score_downloads_d360 },
            { label: 'Downloads D360 US', value: metrics.score_downloads_d360_us },
            { label: 'SOV Downloads D360 US', value: metrics.score_sov_downloads_d360_us },
            { label: 'SOV Revenue D360 US', value: metrics.score_sov_revenue_d360_us },
            { label: 'Growth Revenue', value: metrics.score_growth_revenue },
            { label: 'Growth Revenue US', value: metrics.score_growth_revenue_us },
            { label: 'Growth Downloads', value: metrics.score_growth_downloads },
            { label: 'Growth Downloads US', value: metrics.score_growth_downloads_us },
          ]}
          value={metrics.score_cluster_total}
        />
        <div className="metric-tuple">
          Apps{' '}
          <span className="metric-tuple__value">
            {state.nbVisibleApps} {state.listingMode === 'cluster' ? ` / ${metrics.nb_apps}` : ''}
          </span>
        </div>
        <div className="metric-tuple">
          RPD
          <span className="metric-tuple__value">{metrics.rpd !== null ? `${formatValue(metrics.rpd)}$` : '--'}</span>
        </div>
        <div className="metric-tuple">
          <span>
            RPD <span className="flag-country-icon fi fi-us" />
          </span>
          <span className="metric-tuple__value">
            {' '}
            {metrics.rpd_us !== null ? `${formatValue(metrics.rpd_us)}$` : '--'}
          </span>
        </div>
      </div>
      <div className={detailsClasses}>
        <ScoreTable metrics={metrics} />
      </div>
      <CollapseButton isCollapsed={isTableCollapsed} onClick={handleToggleClick(!isTableCollapsed)} />
    </div>
  ) : (
    <div>No metrics available</div>
  )
}

export default MetricsBlock
